/**
 * Status summaries for a list of devices (tri-server#1364), batched and cached.
 *
 * - One request per list (split only above the server's 500-id limit), never one per device.
 * - Results are cached per tenant and id for {@link STATUS_CACHE_TTL_MS}: the action menu on
 *   the floor plan and the expanded row in the equipment table read what the plan or the table
 *   page already loaded, without a request of their own.
 * - An id already being fetched is not requested again; the second caller waits for the first
 *   request.
 * - Stale entries stay visible while they are refreshed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";

import {
  ASSET_STATUS_SUMMARY_MAX_IDS,
  fetchAssetStatusSummaries,
  type AssetStatusSummary,
} from "../AssetStatusAPI";
import { authenticationAtom } from "../State";

export const STATUS_CACHE_TTL_MS = 60_000;

interface CacheEntry {
  /** `null`: the server left the id out (unknown or not visible to this tenant). */
  summary: AssetStatusSummary | null;
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

const cacheKey = (customer: string, id: string) => `${customer}|${id}`;

/** Tests only: forget everything. */
export function resetAssetStatusSummaryCache() {
  cache.clear();
  inFlight.clear();
}

function notify() {
  listeners.forEach((listener) => listener());
}

function isFresh(entry: CacheEntry | undefined, now: number) {
  return !!entry && now - entry.fetchedAt < STATUS_CACHE_TTL_MS;
}

/**
 * Loads every id that is neither fresh in the cache nor already on its way, in as few
 * requests as the server allows, and resolves once all requested ids are settled.
 */
export async function loadAssetStatusSummaries(
  customer: string,
  ids: string[],
  { force = false }: { force?: boolean } = {},
): Promise<void> {
  const now = Date.now();
  const missing = ids.filter((id) => {
    const key = cacheKey(customer, id);
    return !inFlight.has(key) && (force || !isFresh(cache.get(key), now));
  });

  for (let start = 0; start < missing.length; start += ASSET_STATUS_SUMMARY_MAX_IDS) {
    const chunk = missing.slice(start, start + ASSET_STATUS_SUMMARY_MAX_IDS);
    const request = fetchAssetStatusSummaries(customer, chunk).then((summaries) => {
      const fetchedAt = Date.now();
      const byId = new Map<string, AssetStatusSummary>();
      for (const summary of summaries) {
        if (summary?.asset) byId.set(String(summary.asset), summary);
        if (summary?.id) byId.set(String(summary.id), summary);
      }
      for (const id of chunk) {
        cache.set(cacheKey(customer, id), { summary: byId.get(id) ?? null, fetchedAt });
      }
      notify();
    });
    const settled = request.finally(() => {
      for (const id of chunk) inFlight.delete(cacheKey(customer, id));
    });
    // The per-id entry must not reject unobserved; callers get the error through `pending`.
    settled.catch(() => undefined);
    for (const id of chunk) inFlight.set(cacheKey(customer, id), settled);
  }

  const pending = Array.from(
    new Set(
      ids
        .map((id) => inFlight.get(cacheKey(customer, id)))
        .filter((promise): promise is Promise<void> => !!promise),
    ),
  );
  await Promise.all(pending);
}

export interface AssetStatusSummaryState {
  /** Summary per requested id; ids the server left out are absent. */
  summaries: Record<string, AssetStatusSummary>;
  loading: boolean;
  /** Ids with no cache entry yet: still loading, or the request failed. Settled ids are not here. */
  pendingIds: string[];
  error: unknown;
  reload: () => void;
}

/**
 * Status summaries for `assetIds` (Mongo `_id`s or business ids). Pass the ids of what is on
 * screen — the placed devices of a floor, the visible page of a table.
 */
export function useAssetStatusSummary(
  assetIds: (string | null | undefined)[],
  { enabled = true }: { enabled?: boolean } = {},
): AssetStatusSummaryState {
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string | undefined = authentication?.customer;

  const idsKey = useMemo(
    () =>
      Array.from(new Set(assetIds.filter((id): id is string => !!id).map(String))).join(","),
    [assetIds],
  );
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((current) => current + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !customer || ids.length === 0) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const now = Date.now();
    const needsLoad =
      reloadTick > 0 ||
      ids.some((id) => {
        const key = cacheKey(customer, id);
        return inFlight.has(key) || !isFresh(cache.get(key), now);
      });

    if (!needsLoad) {
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    loadAssetStatusSummaries(customer, ids, { force: reloadTick > 0 })
      .catch((caught) => {
        if (!cancelled) setError(caught ?? new Error("status summary failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customer, ids, enabled, reloadTick]);

  const summaries = useMemo(() => {
    const result: Record<string, AssetStatusSummary> = {};
    if (!customer) return result;
    for (const id of ids) {
      const summary = cache.get(cacheKey(customer, id))?.summary;
      if (summary) result[id] = summary;
    }
    return result;
    // `version` re-reads the cache after any load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, ids, version]);

  const reload = useCallback(() => setReloadTick((tick) => tick + 1), []);

  // Ids never settled count as loading from the first render on, so callers show a skeleton
  // instead of flashing "no status" for the frame before the load effect runs.
  const unsettled =
    enabled && !!customer && !error && ids.some((id) => !cache.has(cacheKey(customer, id)));

  const pendingIds = useMemo(
    () =>
      enabled && customer ? ids.filter((id) => !cache.has(cacheKey(customer, id))) : [],
    // `version` re-reads the cache after any load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, customer, ids, version],
  );

  return { summaries, loading: loading || unsettled, pendingIds, error, reload };
}
